import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  MessageSquare,
  RefreshCw,
  Video,
  Webhook,
} from 'lucide-react'
import {
  appointmentsApi,
  type Appointment,
  type AppointmentPagination,
} from '@/services/appointmentsApi'
import { orderWebhooksApi } from '@/services/orderWebhooksApi'
import ConfirmAppointmentModal from './components/ConfirmAppointmentModal'
import RescheduleAppointmentModal from './components/RescheduleAppointmentModal'
import CancelAppointmentModal from './components/CancelAppointmentModal'
import AppointmentCalendarView from './components/AppointmentCalendarView'
import { useVerticalUI } from '@/lib/verticalUI'

type StatusFilter = 'PENDING' | 'CONFIRMED' | 'all'
type ViewMode = 'list' | 'calendar'

/** SLA visual: verde <30min, amarillo 30min-2h, rojo >2h sin confirmar. */
function slaTone(createdAt: string): { className: string; label: string } {
  const minutes = (Date.now() - new Date(createdAt).getTime()) / 60000
  if (minutes < 30) return { className: 'bg-emerald-100 text-emerald-700', label: 'Recién llegada' }
  if (minutes < 120) return { className: 'bg-amber-100 text-amber-700', label: `${Math.floor(minutes)} min en espera` }
  return { className: 'bg-red-100 text-red-700', label: `${Math.floor(minutes / 60)} h en espera` }
}

function formatSlot(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-VE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function AppointmentCard({
  appointment,
  onConfirm,
  onReschedule,
  onCancel,
}: {
  appointment: Appointment
  onConfirm: (a: Appointment) => void
  onReschedule: (a: Appointment) => void
  onCancel: (a: Appointment) => void
}) {
  const { profile } = useVerticalUI()
  const sla = slaTone(appointment.createdAt)

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-800">
            {appointment.patientName ?? profile.terminology.customerRoleLabel}{' '}
            {appointment.modality === 'REMOTE' ? (
              <Video className="ml-1 inline h-4 w-4 text-sky-500" aria-label="Telemedicina" />
            ) : null}
          </p>
          <p className="truncate text-xs text-slate-500">
            <span className="font-medium text-slate-700">
              {appointment.serviceName ?? profile.terminology.itemSingularName}
            </span>
            {appointment.serviceId ? (
              <Link
                to="/app/products"
                className="ml-1 text-[11px] font-semibold text-emerald-600 hover:underline"
                title="Ver en catálogo"
              >
                (catálogo)
              </Link>
            ) : null}{' '}
            · {appointment.waContact ?? 'sin teléfono'}
          </p>
        </div>
        {appointment.status === 'PENDING' ? (
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${sla.className}`}>
            {sla.label}
          </span>
        ) : (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
              appointment.status === 'CONFIRMED'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            {appointment.status}
          </span>
        )}
      </div>

      <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
        {appointment.status === 'PENDING' ? (
          <p>
            <span className="font-medium text-slate-700">Pidió:</span> “{appointment.requestedSlotText}”
          </p>
        ) : (
          <p>
            <span className="font-medium text-slate-700">Confirmada:</span>{' '}
            {formatSlot(appointment.confirmedSlotStart)}
            {appointment.locationOrRoom ? ` · ${appointment.locationOrRoom}` : ''}
          </p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {appointment.status === 'PENDING' && (
          <>
            <button
              onClick={() => onConfirm(appointment)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              <CalendarCheck className="h-3.5 w-3.5" />
              {profile.terminology.confirmActionLabel}
            </button>
            <button
              onClick={() => onReschedule(appointment)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600"
            >
              <CalendarClock className="h-3.5 w-3.5" />
              Reprogramar
            </button>
            <button
              onClick={() => onCancel(appointment)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
            >
              <CalendarX className="h-3.5 w-3.5" />
              Cancelar
            </button>
          </>
        )}
        {appointment.conversationId && (
          <Link
            to={`/app/conversations/${appointment.conversationId}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Abrir chat
          </Link>
        )}
      </div>
    </div>
  )
}

/**
 * Consola de Citas del personal (fase A3).
 * Bandeja de solicitudes PENDING con SLA visual + confirmación/reprogramación/
 * cancelación manuales (el bot NUNCA confirma: appointment_confirmed_by_bot = 0).
 */
export default function AppointmentsPage() {
  const { profile } = useVerticalUI()
  const pageTitle =
    profile.nav.showAppointments && !profile.nav.showOrders
      ? profile.terminology.primaryTransactionName
      : 'Citas'
  const [filter, setFilter] = useState<StatusFilter>('PENDING')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [pagination, setPagination] = useState<AppointmentPagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasWebhookEndpoints, setHasWebhookEndpoints] = useState(false)

  const [confirming, setConfirming] = useState<Appointment | null>(null)
  const [rescheduling, setRescheduling] = useState<Appointment | null>(null)
  const [cancelling, setCancelling] = useState<Appointment | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await appointmentsApi.getAppointments({ status: filter, limit: 100 })
      setAppointments(result.appointments)
      setPagination(result.pagination)
    } catch {
      setError('No se pudieron cargar las citas')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    let active = true
    void orderWebhooksApi
      .listEndpoints()
      .then((list) => {
        if (active) setHasWebhookEndpoints(Array.isArray(list) && list.length > 0)
      })
      .catch(() => {
        // Silencioso: el enlace discreto es opcional.
      })
    return () => {
      active = false
    }
  }, [])

  // Refresco periódico de la bandeja (los sockets en vivo llegan en A4).
  useEffect(() => {
    const interval = window.setInterval(load, 60_000)
    return () => window.clearInterval(interval)
  }, [load])

  const pendingCount = useMemo(
    () => appointments.filter((a) => a.status === 'PENDING').length,
    [appointments]
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 data-tour="appointments-main" className="text-xl font-bold text-slate-800">{pageTitle}</h1>
          <p className="text-sm text-slate-500">
            Solicitudes anotadas por el bot · la confirmación es manual del personal
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!hasWebhookEndpoints && (
            <Link
              to="/app/order-webhooks"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <Webhook className="h-3.5 w-3.5 text-slate-500" />
              Conectar con tu agenda
            </Link>
          )}
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(
          [
            { key: 'PENDING', label: `Pendientes${pagination.total ? ` (${pendingCount})` : ''}` },
            { key: 'CONFIRMED', label: 'Confirmadas' },
            { key: 'all', label: 'Todas' },
          ] as Array<{ key: StatusFilter; label: string }>
        ).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={[
              'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              filter === tab.key
                ? 'bg-emerald-600 text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
        {profile.operational?.hasAppointmentsCalendar !== false && (
          <div className="ml-auto flex overflow-hidden rounded-lg border border-slate-200">
            {(
              [
                { key: 'list', label: 'Bandeja' },
                { key: 'calendar', label: 'Agenda' },
              ] as Array<{ key: ViewMode; label: string }>
            ).map((mode) => (
              <button
                key={mode.key}
                onClick={() => setViewMode(mode.key)}
                className={[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  viewMode === mode.key
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-600 hover:bg-slate-50',
                ].join(' ')}
              >
                {mode.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>
      )}

      {viewMode === 'calendar' && profile.operational?.hasAppointmentsCalendar !== false ? (
        <div className="mt-4">
          <AppointmentCalendarView
            appointments={appointments}
            onSelect={(appointment) => {
              if (appointment.status === 'PENDING') setConfirming(appointment)
            }}
          />
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {!loading && appointments.length === 0 && (
            <div
              className="col-span-full rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center"
              data-testid="empty-appointments-state"
            >
              <div className="w-12 h-12 bg-emerald-50 text-emerald-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-800">
                No hay {profile.terminology.primaryTransactionPlural.toLowerCase()} en este estado
              </h3>
              <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
                Las solicitudes agendadas por el bot en WhatsApp o creadas manualmente aparecerán aquí.
              </p>
            </div>
          )}
          {appointments.map((appointment) => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              onConfirm={setConfirming}
              onReschedule={setRescheduling}
              onCancel={setCancelling}
            />
          ))}
        </div>
      )}

      {confirming && (
        <ConfirmAppointmentModal
          appointment={confirming}
          onClose={() => setConfirming(null)}
          onConfirmed={() => {
            setConfirming(null)
            load()
          }}
        />
      )}
      {rescheduling && (
        <RescheduleAppointmentModal
          appointment={rescheduling}
          onClose={() => setRescheduling(null)}
          onRescheduled={() => {
            setRescheduling(null)
            load()
          }}
        />
      )}
      {cancelling && (
        <CancelAppointmentModal
          appointment={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null)
            load()
          }}
        />
      )}
    </div>
  )
}
